import { useTranslator } from '@jutro/locale';
import { useAuthentication } from '@xengage/gw-digital-auth-react';
import { useDependencies } from '@xengage/gw-portals-dependency-react';
import { ViewModelForm } from '@xengage/gw-portals-viewmodel-react';
import _ from 'lodash';
import PropTypes from 'prop-types';
import React, { useCallback, useState } from 'react';
import { useModal } from '@jutro/components';
import messages from './EnrollmentComponent.messages';
import metadata from './EnrollmentComponent.metadata.json5';
import styles from './EnrollmentComponent.module.scss';

const ENROLLMENT_ERRORS = {
    enrollmentInvalid: 'enrollmentInvalid',
    enrollmentFailed: 'enrollmentFailed',
    fieldEmpty: 'fieldEmpty',
    policyOrOfferFormatErrorTWIA: 'formatErrorMessage',
    policyOrOfferFormatErrorTFPA: 'formatErrorMessageTFPA',
    dataErrorTWIA: 'invalidDataErrorMessage'
};
const MODAL_COMMON_PROPS = {
    status: 'error',
    icon: 'mi-error-outline',
    cancelButtonText: messages.modalCancelText,
    shouldCloseOnOverlayClick: false
};

const MODAL_MESSAGES = {
    formatError: {
        title: messages.formatErrorTitle,
        message: messages.formatErrorMessage,
       ...MODAL_COMMON_PROPS
    },
    invalidDataError: {
        title: messages.invalidDataErrorTitle,
        message: messages.invalidDataErrorMessage,
        ...MODAL_COMMON_PROPS
    },
    invalidDataDuplicateError: {
        title: messages.invalidDataDuplicateErrorTitle,
        message: messages.invalidDataDuplicateErrorMessage,
        ...MODAL_COMMON_PROPS
    },
    invalidDataAssociationError: {
        title: messages.invalidDataErrorTitle,
        message: messages.enrollmentFailedTWIA,
        ...MODAL_COMMON_PROPS
    },
}

function EnrollmentComponent(props) {
    const translator = useTranslator();
    const { showAlert } = useModal();
    const [formData, updateFormData] = useState({});
    const [enrollmentStatus, setEnrollmentStatus] = useState();
    const [availablePolicies, setAvailablePolicies] = useState([]);
    const { authHeader, user_name, email, authUserData, logout } = useAuthentication();
    const { EnrollmentService } = useDependencies('EnrollmentService');
    const isTFPA = authUserData?.associationName_TWIA === 'tfpa';

    const {
        enrollmentType,
        onSuccessEnrollment,
        informationText,
        informationAdditionalText,
        onReturnLinkClick,
        returnLinkText,
        enrollButtonText,
    } = props;

    const enrollPolicy = useCallback(
        evt => {
            if (evt) {
                evt.preventDefault();
            }

            const { policyNumberOrOfferNumberTwia, postalCodeTwia } = formData;

            if (_.isEmpty(policyNumberOrOfferNumberTwia) || _.isEmpty(postalCodeTwia)) {
                setEnrollmentStatus(ENROLLMENT_ERRORS.fieldEmpty);
                return Promise.resolve();
            }

            return EnrollmentService.addEnrollmentRecord(
                {
                    details: {
                        policyNumberOrOfferNumberTwia: policyNumberOrOfferNumberTwia,
                        postalCodeTwia: postalCodeTwia,
                        additionalEnrollmentUsername: user_name,
                        email
                    },
                    type: enrollmentType,
                },
                authHeader
            )
                .then(resp => {
                    if (onSuccessEnrollment) {
                        onSuccessEnrollment();
                    } else {
                        setAvailablePolicies(formData.policyNumberOrOfferNumberTwia);
                        updateFormData({});
                        setEnrollmentStatus('enrollmentRequestSucceeded');
                        setTimeout(logout, 5000);
                    }
                })
                .catch(error => {
                    setAvailablePolicies([]);
                    const errorCode = _.get(error, 'data.appErrorCode');
                    if (errorCode === 10030) {
                        setEnrollmentStatus(isTFPA ? ENROLLMENT_ERRORS.policyOrOfferFormatErrorTFPA : ENROLLMENT_ERRORS.policyOrOfferFormatErrorTWIA);
                        if (isTFPA) {
                            MODAL_MESSAGES.formatError.title = messages.formatErrorTitleTFPA;
                            MODAL_MESSAGES.formatError.message = messages.formatErrorMessageTFPA;
                        }
                        return showAlert(MODAL_MESSAGES.formatError).then(results => {
                            if (results === 'cancel' || results === 'close') {
                                return _.noop();
                            }
                        }, _.noop);
                    } else if (errorCode === 10041) {
                        setEnrollmentStatus(ENROLLMENT_ERRORS.enrollmentFailed);
                        if (isTFPA) {
                            MODAL_MESSAGES.invalidDataAssociationError.message = messages.enrollmentFailedTFPA;
                        }
                        return showAlert(MODAL_MESSAGES.invalidDataAssociationError).then(results => {
                            if (results === 'cancel' || results === 'close') {
                                return _.noop();
                            }
                        }, _.noop);
                    } else if (errorCode === 10050) {
                        setEnrollmentStatus(ENROLLMENT_ERRORS.enrollmentFailed);
                        const message = translator(
                            isTFPA ? messages.invalidDataDuplicateErrorMessageTFPA : messages.invalidDataDuplicateErrorMessage,
                            {policyOrOfferNumber: policyNumberOrOfferNumberTwia}
                        );
                        MODAL_MESSAGES.invalidDataDuplicateError.message = message;
                        return showAlert(MODAL_MESSAGES.invalidDataDuplicateError).then(results => {
                            if (results === 'cancel' || results === 'close') {
                                return _.noop();
                            }
                        }, _.noop);
                    } else {
                        setEnrollmentStatus(ENROLLMENT_ERRORS.dataErrorTWIA);
                        return showAlert(MODAL_MESSAGES.invalidDataError).then(results => {
                            if (results === 'cancel' || results === 'close') {
                                return _.noop();
                            }
                        }, _.noop);
                    }
                });
        },
        [
            EnrollmentService,
            authHeader,
            enrollmentType,
            formData,
            onSuccessEnrollment,
        ]
    );

    const writeValue = useCallback(
        (value, path) =>
            updateFormData({
                ...formData,
                [path]: value,
            }),
        [formData]
    );

    const getSuccessNotificationMessage = useCallback(() => {
        const policyOrOffer = isTFPA ? 'policy' : /^\d*$/.test(availablePolicies) ? 'offer' : 'policy';
        return (
            <div>
                <span>{translator(messages.enrollmentSuccessBody, {policyOrOffer: policyOrOffer, policyOrOfferNumber: availablePolicies})}</span>
            </div>
        );
    }, [availablePolicies, translator, formData]);

    const overrideProps = {
        verificationInformationText: {
            content: translator(informationText),
        },
        verificationInformationAdditionalText: {
            content: translator(informationAdditionalText),
        },
        enrollmentErrorMessageContainer: {
            visible:
                !_.isUndefined(enrollmentStatus) &&
                enrollmentStatus !== 'enrollmentRequestSucceeded',
        },
        enrollmentErrorMessage: {
            message: translator(messages[enrollmentStatus]),
        },
        enrollmentSuccessNotificationContainer: {
           visible: enrollmentStatus === 'enrollmentRequestSucceeded',
        },
        enrollPolicy: {
           visible: enrollmentStatus !== 'enrollmentRequestSucceeded',
        },
        actionsContainer: {
           visible: enrollmentStatus !== 'enrollmentRequestSucceeded',
        },
        enrollmentSuccessNotification: {
            message:
                availablePolicies.length !== 0
                    ? getSuccessNotificationMessage()
                    : '',
        },
        returnLink: {
            to: _.isUndefined(onReturnLinkClick) ? '/home' : '#',
            content: translator(returnLinkText),
            onClick: onReturnLinkClick,
        },
        enrollButton: {
            content: translator(enrollButtonText),
        },
        policyNumberOrOfferNumberTwia: {
            label: isTFPA ? translator(messages.policyNumberLabel) :  translator(messages.enrollmentPolicyOrOfferNumber),
            tooltip: {
                icon: 'mi-info',
                text: isTFPA ? translator(messages.policyNumberTooltipTextTFPA) : translator(messages.policyNumberOrOfferNumTooltipText),
                placement: 'right',
                trigger : 'mouseenter'
            },
        },
        mailingAddressZipCode: {
            tooltip: {
                icon: 'mi-info',
                text: translator(messages.mailingAddressZipTooltipText),
                placement: 'right',
                trigger: 'mouseenter'
            },
        },
        logoutWarning: {
            className: styles.enrollmentInlineWarning
        },
    };

    const resolvers = {
        resolveClassNameMap: styles,
        resolveCallbackMap: {
            enrollPolicy,
        },
    };

    return (
        <ViewModelForm
            uiProps={metadata.componentContent}
            model={formData}
            overrideProps={overrideProps}
            onValueChange={writeValue}
            callbackMap={resolvers.resolveCallbackMap}
        />
    );
}

EnrollmentComponent.propTypes = {
    enrollmentType: PropTypes.string.isRequired,
    onSuccessEnrollment: PropTypes.func,
    onReturnLinkClick: PropTypes.func,
    returnLinkText: PropTypes.shape({
        id: PropTypes.string,
        defaultMessage: PropTypes.string,
    }),
    enrollButtonText: PropTypes.shape({
        id: PropTypes.string,
        defaultMessage: PropTypes.string,
    }),
    informationText: PropTypes.shape({
        id: PropTypes.string,
        defaultMessage: PropTypes.string,
    }).isRequired,
    informationAdditionalText: PropTypes.shape({
        id: PropTypes.string,
        defaultMessage: PropTypes.string,
    }).isRequired,
};

EnrollmentComponent.defaultProps = {
    onSuccessEnrollment: undefined,
    onReturnLinkClick: undefined,
    returnLinkText: messages.returnToHomePage,
    enrollButtonText: messages.enroll,
};

export default EnrollmentComponent;
